// © 2026 Buddy Media LLC. All rights reserved.
//
// Phase 1 paste-token entry. Operator pastes a BW worker bearer (or
// Anthropic OAuth token) into the field and the SPA stores it in
// memory. The proper Anthropic-OAuth-button flow lands in a Phase
// 2/3 PR after T-602-marc resolves the OAuth-gate strategy.

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthStore } from "@/store/authStore";

export function LoginPage() {
  const setBearer = useAuthStore((s) => s.setBearer);
  const navigate = useNavigate();
  const [token, setToken] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token.trim()) return;
    setSubmitting(true);
    setErr(null);
    setBearer(token.trim());
    // Smoke: confirm /api/me returns 200 before sending the operator
    // into the shell. The backend's CompositeVerifier accepts both
    // BW JWT and Anthropic OAuth bearers.
    try {
      const base =
        (import.meta.env.VITE_BW_BACKEND_URL as string | undefined) ?? "";
      const r = await fetch(`${base}/api/me`, {
        headers: { Authorization: `Bearer ${token.trim()}` },
      });
      if (!r.ok) {
        setBearer(null);
        setErr(`Backend rejected: HTTP ${r.status}`);
        return;
      }
      navigate("/", { replace: true });
    } catch (ex) {
      setBearer(null);
      setErr(
        ex instanceof Error ? ex.message : "Network error reaching backend",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="grid min-h-screen place-items-center px-4">
      <div
        className="w-full max-w-md rounded-xl border p-6"
        style={{
          borderColor: "var(--bw-border)",
          background: "var(--bw-surface)",
        }}
      >
        <div className="mb-4 flex items-center gap-2">
          <span
            aria-hidden
            className="inline-block h-2.5 w-2.5 rounded-full"
            style={{
              background:
                "linear-gradient(135deg, var(--brand-primary) 0%, var(--brand-accent) 100%)",
            }}
          />
          <h1 className="text-xl font-semibold text-white">
            IA Cluster Admin
          </h1>
        </div>
        <p className="mb-5 text-sm text-slate-400">
          Paste a BW worker bearer or Anthropic OAuth token. Phase 1 uses
          paste-entry; the OAuth-button flow ships in a follow-up.
        </p>
        <form onSubmit={submit} className="space-y-4">
          <label className="block">
            <span className="mb-1 block text-xs text-slate-400">
              Bearer token
            </span>
            <input
              type="password"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              placeholder="eyJhbGc... or sk-ant-oat01-..."
              className="w-full rounded-md border bg-slate-900/80 px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-cyan-500/40"
              style={{ borderColor: "var(--bw-border)" }}
              autoFocus
              autoComplete="off"
            />
          </label>
          {err && (
            <p className="text-sm text-rose-300" role="alert">
              {err}
            </p>
          )}
          <button
            type="submit"
            disabled={submitting || !token.trim()}
            className="w-full rounded-md px-4 py-2 text-sm font-semibold text-white shadow-sm transition-all hover:brightness-110 disabled:opacity-50"
            style={{
              background:
                "linear-gradient(135deg, var(--brand-primary) 0%, var(--brand-accent) 100%)",
            }}
          >
            {submitting ? "Verifying…" : "Sign in"}
          </button>
        </form>
      </div>
    </div>
  );
}
